'use client';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Link from 'next/link';
import { signIn } from '@/lib/auth-client';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Card } from '@/components/ui/Card';

const Schema = z.object({ email: z.string().email() });
type Form = z.infer<typeof Schema>;

export default function MagicLinkPage() {
  const [sent, setSent] = useState(false);
  const form = useForm<Form>({ resolver: zodResolver(Schema), defaultValues: { email: '' } });

  const onSubmit = form.handleSubmit(async (values) => {
    // Always show the "check your email" screen regardless of whether the
    // account exists — this prevents user enumeration attacks. The backend
    // silently drops magic-link requests for unknown addresses (disableSignUp).
    await signIn.magicLink({
      email: values.email,
      // Must be an absolute URL so Better Auth can redirect the browser
      // back to the frontend after the backend verifies the magic-link token.
      callbackURL: `${window.location.origin}/dashboard`,
    });
    setSent(true);
  });

  if (sent) {
    return (
      <Card>
        <h2 className="text-2xl font-bold mb-2">Check your email</h2>
        <p className="text-[var(--color-on-surface-variant)]">
          We sent a sign-in link to <strong>{form.getValues('email')}</strong>. It expires in 10
          minutes and can only be used once.
        </p>
      </Card>
    );
  }

  return (
    <Card>
      <h2 className="text-2xl font-bold mb-2">Sign in with magic link</h2>
      <p className="text-[var(--color-on-surface-variant)] mb-6 text-sm">
        We&apos;ll email you a one-time link. No password needed.
      </p>
      <form onSubmit={onSubmit} className="space-y-4">
        <Input
          label="Email"
          type="email"
          {...form.register('email')}
          error={form.formState.errors.email?.message}
          autoComplete="email"
        />
        <Button type="submit" loading={form.formState.isSubmitting} className="w-full">
          Send magic link
        </Button>
      </form>
      <p className="text-sm mt-4 text-[var(--color-on-surface-variant)]">
        Don&apos;t have an account?{' '}
        <Link href="/register" className="font-medium text-[var(--color-primary)]">Sign up</Link>
      </p>
      <p className="text-sm text-center mt-2">
        <Link href="/login">Back to sign in</Link>
      </p>
    </Card>
  );
}
